/*
 * Contact info with form — the "remember me" checkbox only.
 *
 * The contact form itself posts natively and reloads the page, so nothing here
 * touches the submission. The component saves the visitor's name and email to
 * localStorage when the box is ticked and prefills them on the next visit,
 * mirroring the storage handling in popup-newsletter.js: every access is
 * wrapped, because private browsing and blocked cookies make storage throw.
 */

const STORAGE_KEY = 'amerce_contact_info_form';

class ContactInfoForm extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.form = this.querySelector('form');
    this.nameInput = this.querySelector('[data-contact-info-name]');
    this.emailInput = this.querySelector('[data-contact-info-email]');
    this.checkbox = this.querySelector('[data-contact-info-remember]');

    if (!this.form || !this.checkbox) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    /* `submit` bubbles, so one listener on the host covers the whole form. */
    this.addEventListener('submit', this.onSubmit.bind(this), { signal });

    this.restore();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  /*
   * A value already in the field wins: after a failed post Liquid echoes back
   * what the visitor just typed, and a logged-in customer gets their account
   * details — neither should be overwritten by an older saved value.
   */
  restore() {
    const saved = this.read();
    if (!saved) return;

    this.checkbox.checked = true;

    if (this.nameInput && !this.nameInput.value && saved.name) {
      this.nameInput.value = saved.name;
    }

    if (this.emailInput && !this.emailInput.value && saved.email) {
      this.emailInput.value = saved.email;
    }
  }

  onSubmit() {
    if (this.checkbox.checked) {
      this.write({
        name: this.nameInput ? this.nameInput.value : '',
        email: this.emailInput ? this.emailInput.value : '',
      });
    } else {
      this.clear();
    }
  }

  read() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;

      const parsed = JSON.parse(stored);
      if (!parsed || typeof parsed !== 'object') return null;

      return {
        name: typeof parsed.name === 'string' ? parsed.name : '',
        email: typeof parsed.email === 'string' ? parsed.email : '',
      };
    } catch (error) {
      /* Unreadable or unavailable storage — the fields simply start empty. */
      return null;
    }
  }

  write(value) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch (error) {
      /* Nothing to recover from — the preference just is not remembered. */
    }
  }

  clear() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      /* As above. */
    }
  }
}

if (!customElements.get('contact-info-form')) {
  customElements.define('contact-info-form', ContactInfoForm);
}
